import { audioContext } from './context';

/** Salamander Grand Piano, every minor third from A0 to C8 (see public/samples/salamander). */
export const SAMPLE_BASE_URL = '/samples/salamander/';
export const PIANO_CACHE_NAME = 'corocanto-piano-salamander-v1';

const NAMES = ['C', 'Ds', 'Fs', 'A'] as const;
const SEMITONES: Record<(typeof NAMES)[number], number> = { C: 0, Ds: 3, Fs: 6, A: 9 };

/** Sample file names with their MIDI pitch: A0 (21), C1 (24), Ds1, Fs1, A1, … C8 (108). */
export const PIANO_SAMPLES: readonly { file: string; midi: number }[] = (() => {
  const list: { file: string; midi: number }[] = [{ file: 'A0.mp3', midi: 21 }];
  for (let octave = 1; octave <= 7; octave++) {
    for (const name of NAMES) list.push({ file: `${name}${octave}.mp3`, midi: (octave + 1) * 12 + SEMITONES[name] });
  }
  list.push({ file: 'C8.mp3', midi: 108 });
  return list;
})();

/**
 * Fetch a sample, from Cache Storage when it is there. The first visit downloads the samples
 * once; later visits (and rehearsals without internet) read them from the cache.
 */
async function fetchSample(url: string): Promise<ArrayBuffer> {
  if (typeof caches !== 'undefined') {
    try {
      const cache = await caches.open(PIANO_CACHE_NAME);
      const cached = await cache.match(url);
      if (cached) return await cached.arrayBuffer();
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${url}: ${response.status}`);
      await cache.put(url, response.clone());
      return await response.arrayBuffer();
    } catch (e) {
      if (e instanceof Error && e.message.startsWith(url)) throw e;
      // Cache Storage unavailable (private mode, quota): fall back to a plain download.
    }
  }
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  return response.arrayBuffer();
}

export type PianoBuffers = Map<number, AudioBuffer>;

let loading: Promise<PianoBuffers> | null = null;

/**
 * Decoded piano samples by MIDI pitch, loaded once per page session and shared by every channel.
 * `onProgress` reports how many of the samples are ready.
 */
export function loadPianoBuffers(onProgress?: (loaded: number, total: number) => void): Promise<PianoBuffers> {
  if (!loading) {
    const ctx = audioContext();
    let done = 0;
    loading = Promise.all(
      PIANO_SAMPLES.map(async ({ file, midi }) => {
        const buffer = await ctx.decodeAudioData(await fetchSample(SAMPLE_BASE_URL + file));
        done++;
        onProgress?.(done, PIANO_SAMPLES.length);
        return [midi, buffer] as const;
      }),
    ).then((entries) => new Map(entries));
    loading.catch(() => {
      loading = null;
    });
  } else if (onProgress) {
    void loading.then(() => onProgress(PIANO_SAMPLES.length, PIANO_SAMPLES.length));
  }
  return loading;
}
